<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {route} from "ziggy-js";
import {usePage} from "@inertiajs/vue3";
import Card from "@/Components/Card.vue";
import Collapse from "@/Components/Collapse.vue";
import ChannelTabs from "@/Components/CommunityChat/ChannelTabs.vue";
import MessageList from "@/Components/CommunityChat/MessageList.vue";
import {chatChannels} from "@/chatChannels.js";
import {loadEcho} from "@/helper.js";
import {LOCALES} from "@/constants.js";

const page = usePage()

// The chat is a Russian-language surface: every message in it is written by (and
// for) the RU audience, so on any other locale it reads as untranslated content
// rather than a sign of a live marketplace. The gate lives here rather than at
// the three call sites (Index, Category/Show, Product/Show) so it also suppresses
// this component's mount side effects - the `chat.messages.latest` request and the
// always-on Echo subscription to every channel - which a `v-if` on the markup
// alone would still leave running.
const isAvailable = computed(() => page.props.lang === LOCALES.RU)

const expanded = ref(false)
const isFullscreen = ref(false)
const activeChannel = ref('general')
const lastMessage = ref(null)

// Entering fullscreen implies the accordion is "open" too, so the inline MessageList
// doesn't stay mounted (and double-subscribed to Echo) underneath the teleported overlay.
const enterFullscreen = () => {
    expanded.value = true
    isFullscreen.value = true
}
const exitFullscreen = () => {
    isFullscreen.value = false
}

const closeFullscreenOnEscape = (event) => {
    if (isFullscreen.value && event.key === 'Escape') exitFullscreen()
}

watch(isFullscreen, (value) => {
    if (typeof document === 'undefined') return
    document.body.style.overflow = value ? 'hidden' : null
})

const handleLastMessageUpdate = (message) => {
    // response()->json(null) serializes to "{}", not "null" - an empty object is
    // truthy in JS, so check for an actual id rather than just truthiness.
    if (!message?.id) return
    if (!lastMessage.value || new Date(message.created_at) >= new Date(lastMessage.value.created_at)) {
        lastMessage.value = message
    }
}

// The widget only mounts a MessageList (and its own per-channel Echo subscription)
// once expanded, so the collapsed preview needs its own always-on subscription
// across every channel to notice messages sent by other users while collapsed.
// Echo caches/shares channel instances by name, and MessageList may be listening
// on the same channel at the same time - so cleanup must remove only this
// listener (stopListening), never the whole channel (leave), or one would kill
// the other's subscription too.
const echoChannelNames = chatChannels.map(c => 'community-chat.' + c.value)

onMounted(() => {
    if (!isAvailable.value) return

    axios.get(route('chat.messages.latest')).then(({data}) => handleLastMessageUpdate(data))

    loadEcho(page.props.reverb, () => {
        echoChannelNames.forEach(name => {
            window.Echo.channel(name).listen('.ChatMessageCreated', handleLastMessageUpdate)
        })
    })

    document.addEventListener('keydown', closeFullscreenOnEscape)
})

onUnmounted(() => {
    if (!isAvailable.value) return

    if (window.Echo) {
        echoChannelNames.forEach(name => {
            window.Echo.channel(name).stopListening('.ChatMessageCreated', handleLastMessageUpdate)
        })
    }

    document.removeEventListener('keydown', closeFullscreenOnEscape)
    document.body.style.overflow = null
})
</script>

<template>
    <Card v-if="isAvailable">
        <Collapse v-model="expanded" class="px-2.5">
            <template #header>
                <div class="flex items-center gap-2 min-w-0 grow">
                    <img src="/assets/img/referral-landing/advantages/3.gif" alt=""
                         class="w-6 h-6 shrink-0"/>
                    <div class="text-xl dark:text-slate-200 font-bold shrink-0 leading-120">
                        {{ $t('Community Chat') }}
                    </div>
                    <div v-if="lastMessage && !expanded" class="text-sm text-slate-500 dark:text-slate-400 truncate min-w-0">
                        <span class="font-medium">{{ lastMessage.is_admin_message ? $t('Administration') : lastMessage.user?.name }}:</span>
                        {{ lastMessage.type === 'gif' ? $t('GIF') : lastMessage.body }}
                    </div>
                    <button type="button" :title="$t('Fullscreen')" :aria-label="$t('Fullscreen')"
                            class="flex items-center justify-center shrink-0 ml-auto mr-2 opacity-70 hover:opacity-100 transition-opacity"
                            @click.stop="enterFullscreen">
                        <img src="/assets/icons/fullscreen-enter.svg" alt="" class="w-6 h-6"/>
                    </button>
                </div>
            </template>
            <div class="flex flex-col gap-2.5 pb-3.5 px-0.5">
                <ChannelTabs v-model="activeChannel"/>
                <MessageList v-if="expanded && !isFullscreen" :channel="activeChannel" @update:last-message="handleLastMessageUpdate"/>
            </div>
        </Collapse>
    </Card>

    <Teleport to="body">
        <div v-if="isFullscreen" class="fixed inset-0 z-50 bg-white dark:bg-dark-blue flex flex-col">
            <div class="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200 dark:border-slate-700 shrink-0">
                <img src="/assets/img/referral-landing/advantages/3.gif" alt=""
                     class="w-6 h-6 shrink-0"/>
                <div class="text-xl dark:text-slate-200 font-bold leading-120">
                    {{ $t('Community Chat') }}
                </div>
                <button type="button" :title="$t('Exit fullscreen')" :aria-label="$t('Exit fullscreen')"
                        class="flex items-center justify-center shrink-0 ml-auto mr-2 opacity-70 hover:opacity-100 transition-opacity"
                        @click="exitFullscreen">
                    <img src="/assets/icons/fullscreen-exit.svg" alt="" class="w-6 h-6"/>
                </button>
            </div>
            <div class="flex flex-col gap-2.5 px-2.5 pt-2.5 pb-2.5 flex-1 min-h-0">
                <ChannelTabs v-model="activeChannel" class="shrink-0"/>
                <MessageList v-if="isFullscreen" :channel="activeChannel" fullscreen class="flex-1 min-h-0"
                             @update:last-message="handleLastMessageUpdate"/>
            </div>
        </div>
    </Teleport>
</template>
